import { collection, doc, setDoc, deleteDoc, query, onSnapshot, getDocs, Unsubscribe } from 'firebase/firestore';
import { db } from './firebase';
import { DocumentCategory, Product } from '../types';
import { slugify } from './slugBase';
import { perfTracker } from './perfLogger';

export interface CategoryModel {
  id: string;
  name: DocumentCategory | string;
  slug: string;
  iconName: string;
  gradient?: string;
  accentColor?: string;
  description: string;
  seoTitle?: string;
  seoDescription?: string;
  status: 'active' | 'hidden';
  displayOrder: number;
  keywords?: string[];
  synonyms?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export const INITIAL_CATEGORIES: CategoryModel[] = [
  {
    id: 'cat-car-title',
    name: 'Car Title',
    slug: 'car-title',
    iconName: 'Car',
    gradient: 'from-rose-500 via-red-500 to-pink-500',
    accentColor: '#f43f5e',
    description: 'Vehicle title and registration certificate templates.',
    seoTitle: 'Car Title PSD Templates & Vehicle Registration | RH Editography',
    seoDescription: 'High quality editable vehicle titles, car registration certificates, and automobile ownership documents.',
    status: 'active',
    displayOrder: 6.5,
    keywords: ['car', 'title', 'vehicle', 'registration', 'automobile', 'certificate'],
    synonyms: ['vehicle title', 'car certificate', 'auto title']
  },
  {
    id: 'cat-passport',
    name: 'Passport',
    slug: 'passport',
    iconName: 'BookOpen',
    gradient: 'from-blue-600 via-indigo-600 to-cyan-500',
    accentColor: '#3b82f6',
    description: 'Biometric & standard global passport layout PSD & AI vector templates.',
    seoTitle: 'Biometric Passport PSD Templates & Vector Layouts | RH Editography',
    seoDescription: 'Download high-resolution 3D editable biometric passport vector layouts, PSD templates, and smart object photo layers.',
    status: 'active',
    displayOrder: 1,
    keywords: ['passport', 'pass', 'biometric', 'travel', 'visa', 'mrz', 'booklet'],
    synonyms: ['travel document', 'biometric pass', 'passports', 'mrz passport']
  },
  {
    id: 'cat-id-card',
    name: 'ID Card',
    slug: 'id-card',
    iconName: 'CreditCard',
    gradient: 'from-purple-600 via-violet-600 to-indigo-500',
    accentColor: '#8b5cf6',
    description: 'National identity, corporate employee & PVC smart card vector designs.',
    seoTitle: 'ID Card Templates, CR80 PVC Smart Cards PSD | RH Editography',
    seoDescription: 'Editable national identity cards, corporate employee ID badges, and CR80 PVC smart card PSD templates.',
    status: 'active',
    displayOrder: 2,
    keywords: ['id', 'card', 'identity', 'national id', 'pvc', 'badge', 'smart card'],
    synonyms: ['id card', 'cards', 'national identity', 'employee badge', 'nid']
  },
  {
    id: 'cat-driving-license',
    name: 'Driving License',
    slug: 'driving-license',
    iconName: 'Car',
    gradient: 'from-cyan-600 via-teal-600 to-emerald-500',
    accentColor: '#06b6d4',
    description: 'International & state driver permit templates with smart layer objects.',
    seoTitle: 'Driver License PSD Templates & Permit Vector Files | RH Editography',
    seoDescription: 'High quality driver license PSD templates, state driving permits, and international driver identity vectors.',
    status: 'active',
    displayOrder: 3,
    keywords: ['driving', 'license', 'driver', 'permit', 'dl', 'dmv', 'auto'],
    synonyms: ['driver license', 'driving permit', 'driver card', 'drivers license']
  },
  {
    id: 'cat-certificate',
    name: 'Certificate',
    slug: 'certificate',
    iconName: 'Award',
    gradient: 'from-amber-500 via-orange-600 to-yellow-500',
    accentColor: '#f59e0b',
    description: 'Premium academic, executive award & ISO certification vector layouts.',
    seoTitle: 'Editable Certificate PSD Templates & Gold Foil Layouts | RH Editography',
    seoDescription: 'Download professional degree certificates, executive award templates, and ISO security border vector designs.',
    status: 'active',
    displayOrder: 4,
    keywords: ['certificate', 'cert', 'degree', 'diploma', 'award', 'iso', 'foil'],
    synonyms: ['diploma', 'academic certificate', 'award certificate', 'degree']
  },
  {
    id: 'cat-utility-bill',
    name: 'Utility Bill',
    slug: 'utility-bill',
    iconName: 'FileText',
    gradient: 'from-sky-600 via-blue-600 to-indigo-600',
    accentColor: '#0284c7',
    description: 'Electricity, water, gas & fiber broadband statement mockups.',
    seoTitle: 'Utility Bill Templates PSD, Electricity & Water Statement | RH Editography',
    seoDescription: 'Editable utility bill mockups, electricity bill PSD templates, broadband fiber & gas statement vectors.',
    status: 'active',
    displayOrder: 5,
    keywords: ['utility', 'bill', 'electricity', 'water', 'gas', 'broadband', 'invoice'],
    synonyms: ['utility bill', 'electric bill', 'gas statement', 'water bill', 'bills']
  },
  {
    id: 'cat-bank-statement',
    name: 'Bank Statement',
    slug: 'bank-statement',
    iconName: 'Landmark',
    gradient: 'from-emerald-600 via-teal-600 to-cyan-600',
    accentColor: '#10b981',
    description: 'Comprehensive financial, corporate transaction & audit statement layouts.',
    seoTitle: 'Bank Statement PSD Templates & Financial Account Layouts | RH Editography',
    seoDescription: 'Professional bank statement templates, financial audit balance sheets, and transaction log vector formats.',
    status: 'active',
    displayOrder: 6,
    keywords: ['bank', 'statement', 'financial', 'audit', 'banking', 'account', 'balance'],
    synonyms: ['bank statement', 'account statement', 'financial report', 'bank log']
  },
  {
    id: 'cat-resume',
    name: 'Resume',
    slug: 'resume',
    iconName: 'UserCheck',
    gradient: 'from-blue-500 via-cyan-500 to-teal-400',
    accentColor: '#0ea5e9',
    description: 'Modern executive, tech lead & creative portfolio CV templates.',
    seoTitle: 'Modern CV & Resume Templates PSD DOCX | RH Editography',
    seoDescription: 'Clean executive resume templates, curriculum vitae vectors, and job application portfolio layouts.',
    status: 'active',
    displayOrder: 7,
    keywords: ['resume', 'cv', 'portfolio', 'curriculum', 'job', 'bio', 'executive'],
    synonyms: ['cv', 'curriculum vitae', 'job resume', 'resumes']
  },
  {
    id: 'cat-health-id',
    name: 'Health ID',
    slug: 'health-id',
    iconName: 'Activity',
    gradient: 'from-rose-500 via-pink-500 to-red-600',
    accentColor: '#f43f5e',
    description: 'Editable Health ID card templates, medical records & healthcare identity documents.',
    seoTitle: 'Health ID Card Templates PSD & Vector | RH Editography',
    seoDescription: 'Download editable Health ID card templates, medical identity vectors, and healthcare document layouts.',
    status: 'active',
    displayOrder: 8,
    keywords: ['health', 'id', 'medical', 'identity', 'healthcare', 'card', 'document'],
    synonyms: ['health id', 'medical card', 'health card']
  },
  {
    id: 'cat-residence-permit',
    name: 'Residence Permit',
    slug: 'residence-permit',
    iconName: 'Layers',
    gradient: 'from-pink-600 via-rose-600 to-purple-600',
    accentColor: '#ec4899',
    description: 'Permanent residence, work permit & green card vector layouts.',
    seoTitle: 'Residence Permit PSD Templates & Vector Layouts | RH Editography',
    seoDescription: 'Download editable residence permit templates, work visas, and green card vector formats.',
    status: 'active',
    displayOrder: 9,
    keywords: ['residence', 'permit', 'green card', 'visa', 'work permit', 'pr'],
    synonyms: ['residence card', 'work visa', 'pr card']
  },
  {
    id: 'cat-visa-documents',
    name: 'Visa Documents',
    slug: 'visa-documents',
    iconName: 'Globe',
    gradient: 'from-fuchsia-600 via-purple-600 to-pink-500',
    accentColor: '#d946ef',
    description: 'Schengen, US, UK tourist & business visa invitation guarantee letters.',
    seoTitle: 'Visa Document Templates, Schengen & US Visa PSD Vectors | RH Editography',
    seoDescription: 'Download visa invitation templates, embassy guarantee letters, and international travel document vectors.',
    status: 'active',
    displayOrder: 10,
    keywords: ['visa', 'embassy', 'schengen', 'guarantee', 'invitation', 'travel', 'border'],
    synonyms: ['visa docs', 'visa letter', 'embassy document', 'visas']
  },
  {
    id: 'cat-others',
    name: 'Others',
    slug: 'others',
    iconName: 'FolderPlus',
    gradient: 'from-gray-600 via-slate-700 to-zinc-800',
    accentColor: '#64748b',
    description: 'Specialized security seals, barcode generators & vector badge assets.',
    seoTitle: 'Security Stamps, Vector Badges & Custom Templates | RH Editography',
    seoDescription: 'Explore specialized security seal vectors, barcode overlays, and custom digital document assets.',
    status: 'active',
    displayOrder: 11,
    keywords: ['other', 'misc', 'security', 'seal', 'stamp', 'badge', 'barcode'],
    synonyms: ['miscellaneous', 'other documents', 'extra templates']
  }
];

const CATEGORIES_COLLECTION = 'categories';
const CACHE_KEY = 'rh_cached_categories';

export function setLocalCachedCategories(categories: CategoryModel[]): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(categories));
  } catch (e) {}
}

export function getLocalCachedCategories(): CategoryModel[] {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 5) {
        return parsed;
      }
    }
  } catch (e) {}
  return INITIAL_CATEGORIES;
}

export function getCategoryPublishedCountFromProducts(products: Product[], categoryName: string): number {
  if (!products || !Array.isArray(products)) return 0;
  return products.filter(p => {
    const isCatMatch = p.category && p.category.toLowerCase().trim() === categoryName.toLowerCase().trim();
    const isPublished = p.status === undefined || p.status === 'published';
    return isCatMatch && isPublished;
  }).length;
}

const categorySubscribers = new Set<(categories: CategoryModel[]) => void>();
let activeCategoriesUnsub: Unsubscribe | null = null;
let lastCategoryData: CategoryModel[] | null = null;

export async function fetchCategoriesFromFirestore(): Promise<CategoryModel[]> {
  const startTime = performance.now();
  try {
    const q = query(collection(db, CATEGORIES_COLLECTION));
    const snapshot = await getDocs(q);
    const duration = performance.now() - startTime;

    let fetched: CategoryModel[] = [];
    if (!snapshot.empty) {
      snapshot.forEach((docSnap) => {
        fetched.push({ id: docSnap.id, ...docSnap.data() } as CategoryModel);
      });
    }

    const finalMap = new Map<string, CategoryModel>();
    INITIAL_CATEGORIES.forEach(cat => {
      finalMap.set(cat.id, { ...cat });
    });
    
    fetched.forEach(cat => {
      if (cat.id) {
         finalMap.set(cat.id, cat);
      }
    });

    const finalCats = Array.from(finalMap.values());
    finalCats.sort((a, b) => (a.displayOrder || 99) - (b.displayOrder || 99));

    setTimeout(() => {
      try {
        finalCats.forEach(cat => {
          if (!fetched.find(f => f.id === cat.id)) {
            setDoc(doc(db, CATEGORIES_COLLECTION, cat.id), cat).catch(()=>{});
          }
        });
        
        // --- Migration: Remove old "cat-templates" if it exists in Firestore ---
        if (fetched.some(f => f.id === 'cat-templates')) {
          deleteDoc(doc(db, CATEGORIES_COLLECTION, 'cat-templates')).catch(()=>{});
          // Remove from finalCats so UI doesn't render it before reload
          const index = finalCats.findIndex(f => f.id === 'cat-templates');
          if (index !== -1) finalCats.splice(index, 1);
        }
        // -------------------------------------------------------------------------

      } catch (e) {}
    }, 2000);

    try {
        perfTracker.logFetch(CATEGORIES_COLLECTION, duration, snapshot.empty ? 'server' : 'cache', finalCats.length);
    } catch (e) {}

    lastCategoryData = finalCats;
    setLocalCachedCategories(finalCats);
    
    return finalCats;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return INITIAL_CATEGORIES;
  }
}

export function subscribeToCategoriesFromFirestore(onUpdate: (categories: CategoryModel[]) => void): () => void {
  const cached = lastCategoryData || getLocalCachedCategories();
  onUpdate(cached);

  categorySubscribers.add(onUpdate);

  if (!activeCategoriesUnsub) {
    const q = query(collection(db, CATEGORIES_COLLECTION));
    activeCategoriesUnsub = onSnapshot(q, (snapshot) => {
      let fetched: CategoryModel[] = [];
      snapshot.forEach((docSnap) => {
        fetched.push({ id: docSnap.id, ...docSnap.data() } as CategoryModel);
      });

      const finalMap = new Map<string, CategoryModel>();
      INITIAL_CATEGORIES.forEach(cat => {
        finalMap.set(cat.id, { ...cat });
      });
      fetched.forEach(cat => {
        if (cat.id && cat.id !== 'cat-templates') {
           finalMap.set(cat.id, cat);
        }
      });
      if (finalMap.has('cat-templates')) finalMap.delete('cat-templates');
      const finalCats = Array.from(finalMap.values());
      finalCats.sort((a, b) => (a.displayOrder || 99) - (b.displayOrder || 99));

      lastCategoryData = finalCats;
      try {
        if (typeof window !== 'undefined') {
          localStorage.setItem(CACHE_KEY, JSON.stringify(finalCats));
        }
      } catch (e) {}

      categorySubscribers.forEach((cb) => {
        try { cb(finalCats); } catch (e) {}
      });
    }, (err) => {
      console.warn('Realtime categories fetch error:', err);
    });
  } else if (lastCategoryData) {
    onUpdate(lastCategoryData);
  }

  return () => {
    categorySubscribers.delete(onUpdate);
  };
}

export async function saveCategoryToFirestore(category: Partial<CategoryModel> & { name: string }): Promise<void> {
  try {
    const slug = category.slug ? slugify(category.slug) : slugify(category.name);
    const catId = category.id || `cat-${slug}`;
    const docData: CategoryModel = {
      id: catId,
      name: category.name,
      slug,
      iconName: category.iconName || 'FolderPlus',
      gradient: category.gradient || 'from-blue-600 to-indigo-600',
      accentColor: category.accentColor || '#3b82f6',
      description: category.description || `${category.name} digital templates.`,
      seoTitle: category.seoTitle || `${category.name} PSD Templates & Vector Layouts | RH Editography`,
      seoDescription: category.seoDescription || `High quality ${category.name} templates with editable PSD and vector files.`,
      status: category.status || 'active',
      displayOrder: category.displayOrder || 10,
      keywords: category.keywords || [slug, category.name.toLowerCase()],
      synonyms: category.synonyms || [category.name.toLowerCase()],
      updatedAt: new Date().toISOString()
    };

    await setDoc(doc(db, CATEGORIES_COLLECTION, catId), docData, { merge: true });
  } catch (err) {
    console.error('Error saving category to Firestore:', err);
  }
}

export async function deleteCategoryFromFirestore(catId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, CATEGORIES_COLLECTION, catId));
  } catch (err) {
    console.error('Error deleting category from Firestore:', err);
  }
}

export function searchCategories(categories: CategoryModel[], searchTerm: string): CategoryModel[] {
  if (!searchTerm.trim()) return categories.filter(c => c.status !== 'hidden');

  const term = searchTerm.toLowerCase().trim();
  const cleanTerm = term.replace(/[^a-z0-9]/g, '');

  return categories.filter((cat) => {
    if (cat.status === 'hidden') return false;

    const catName = cat.name.toLowerCase();
    const catSlug = cat.slug.toLowerCase();
    const catDesc = cat.description.toLowerCase();

    if (catName.includes(term) || catSlug.includes(term) || catDesc.includes(term)) return true;

    const cleanCatName = catName.replace(/[^a-z0-9]/g, '');
    if (cleanCatName.includes(cleanTerm) || cleanTerm.includes(cleanCatName)) return true;

    if (cat.synonyms && cat.synonyms.some(s => s.toLowerCase().includes(term) || term.includes(s.toLowerCase()))) {
      return true;
    }

    if (cat.keywords && cat.keywords.some(k => k.toLowerCase().includes(term) || term.includes(k.toLowerCase()))) {
      return true;
    }

    if (cleanTerm.length >= 3) {
      if (catName.startsWith(cleanTerm.slice(0, 3))) return true;
    }

    return false;
  });
}
